import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TenantService } from '@/services/api/tenant';
import { useTenantStore } from '@/state/tenant_state';

export const MY_TENANTS_QUERY_KEY = ['my-tenants'];

export const fetchMyTenants = () => TenantService.getInstance().getMyTenants();

export function useMyTenants() {
    return useQuery({
        queryKey: MY_TENANTS_QUERY_KEY,
        queryFn: fetchMyTenants,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
    });
}

/** Reloads the user's tenants on app start and window focus so newly enabled features show up without reselecting the tenant. */
export function useRefreshSelectedTenant() {
    const hasSelectedTenant = useTenantStore((state) => state.selectedTenant !== null);
    const refreshSelectedTenant = useTenantStore((state) => state.refreshSelectedTenant);
    const { data, refetch } = useQuery({
        queryKey: MY_TENANTS_QUERY_KEY,
        queryFn: fetchMyTenants,
        enabled: hasSelectedTenant,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        retry: 2,
    });

    useEffect(() => {
        if (data) refreshSelectedTenant(data);
    }, [data, refreshSelectedTenant]);

    useEffect(() => {
        if (!hasSelectedTenant) return;
        const onFocus = () => {
            if (document.visibilityState === 'visible') void refetch({ cancelRefetch: false });
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [hasSelectedTenant, refetch]);
}
